﻿import { usePriceFormat } from '@/hooks/usePriceFormat';
import type {
    BadgeProps
} from '@chakra-ui/react';
import {
    Badge,
    Box,
    Button,
    Flex,
    HStack,
    Icon,
    Link,
    ListItem,
    OrderedList,
    Stack,
    Text,
    VStack,
} from '@chakra-ui/react';
import { faFileLines, faPaperPlane, faPen } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import CustomTooltip from 'BrokerTsLib/src/components/CustomTooltip';
import type {
    IInsuranceParams,
    InsuranceBase,
    InsuranceParamsLeasingDto,
    InsuranceParamsTravelDto,
    Offer,
    OfferTravel,
    SendContractEmailResponse
} from 'BrokerTsLib/src/dtos';
import {
    getDigitalSignatureInstructions,
    getDigitalSignatureSecondInstructions
} from 'BrokerTsLib/src/dtos';
import { ContractAcceptationType, getPolicyStatusName, Insurer, PolicyStatusType, SelectionType } from 'BrokerTsLib/src/enums';
import { useCustomToast } from 'BrokerTsLib/src/hooks/useCustomToast';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import * as QRCodeModule from 'react-qr-code';
const QRCode = (QRCodeModule as any).default?.default ?? (QRCodeModule as any).default

import { CancelContract } from '@/components/CancelContract';
import { CreateStockPs } from '@/components/CreateStockPs';
import { WarningIcon } from '@/components/icons/WarningIcon';
import { getPackageRecapData, getRecapData } from '@/components/Recap/RecapQuote';
import { VwfsStornoActions } from '@/components/VwfsStornoActions';
import { SendContractContextProvider, SendContractModal, useSendContractContext } from '@/containers/steps/SendContractModal';
import { useMeetingRecord } from '@/context/MeetingRecordProvider';
import { usePartner } from '@/context/PartnerContext';
import { Apps } from '@/dtos';
import { useApp } from '@/hooks/useApp';
import { useGetInsurance } from '@/hooks/useInsuranceParams';
import { useSendContractEmail } from '@/hooks/useMailing';
import { useConfirmContract, useGetIsContractConfirmed, useGetManualStornoContractsInfo } from '@/hooks/useWizard';

import { CompletedOverviewValue, type CompletedOverviewItemProps } from '@/components/CompletedOverview/CompletedOverviewItem';
import { Documents } from '@/components/Documents';
import { ReactivateContract } from '@/components/ReactivateContract';
import { useInsuranceParams } from '@/context/InsuranceParamsProvider';
import { InsuranceSagaProvider } from '@/context/InsuranceSagaContext';
import { useSelections } from '@/hooks';
import { useGetVehicleInspection } from '@/hooks/useOffer';
import {
    formatDate,
    formatDateTime,
    formatDateTimeConditional,
} from '@/utils/formatDate';
import type { AxiosError } from 'axios';
import { Information } from './Information';

const InfoBadge = ({ children }: BadgeProps) => {
  return (
    <Badge
      sx={{
        w: 6,
        h: 6,
        display: 'inline-grid',
        placeItems: 'center',
        fontWeight: 'medium',
        flexShrink: 0,
      }}
      variant="solid"
      bgColor='primary.800'
    >
      {children}
    </Badge>
  )
}

export function QuoteInformation({ offer, additionalInfoData: initialAdditionalInfoData = [] }: { offer: Offer, additionalInfoData?: CompletedOverviewItemProps[] }) {
  const { app } = useApp()
  const { priceFormat, currency } = usePriceFormat()
  const isPublicPoleas = app == Apps.publicPoleas

  const { insuranceParams, insuranceParamsId, refetchInsuranceParams, isContracted } = useInsuranceParams()
  const ipt = insuranceParams as InsuranceParamsTravelDto
  const { meetingRecord } = useMeetingRecord()


  const { data: vehicleInspectionData } = useGetVehicleInspection(offer.offerId, {
    enabled: app == Apps.vehicle
  })
  const { data: dataFs } = useSelections(SelectionType.FormaSjednani)
  const fsEnum = dataFs?.data ?? []
  const { data: dataDp } = useSelections(SelectionType.ZpusobDigitalnihoPodpisu)
  const dpEnum = dataDp?.data ?? []
  const cat = insuranceParams!.generalParams.contractAcceptationType

  const [contractConfirmInfo, setContractConfirmInfo] = useState<string | null>(null)
  const [contractConfirmed, setContractConfirmed] = useState<boolean>(false)

  const { mutateAsync: confirmContractAsync, isPending: isConfirmingContract } = useConfirmContract()
  const { refetch: fetchIsContractConfirmed } = useGetIsContractConfirmed(insuranceParams?.generalParams.paramsId, { enabled: false })

  useEffect(() => {
    if (insuranceParams && offer.insurerId == Insurer.Csob && cat === ContractAcceptationType.DigitalSignature) {
      fetchIsContractConfirmed().
        then((resp) => {
          if (resp.data?.data?.success) {
            setContractConfirmed(true)
          }
        })
    }
  }, [insuranceParams])

  const {
    data: dataInsurance,
    refetch: refetchInsurance,
    isLoading: isLoadingInsurance,
  } = useGetInsurance(offer.offerId)
  const insurance = dataInsurance?.data ?? {}
  const isCanceled = !!insurance.contractCanceled

  const onContractChange = useCallback(() => {
    refetchInsuranceParams()
    refetchInsurance()
  }, [refetchInsuranceParams, refetchInsurance])

  const { currentPartner } = usePartner()

  const isVwfs = currentPartner?.partner == 'VWFS'
  const leasingParams = insuranceParams as InsuranceParamsLeasingDto
  const lcs = leasingParams?.leasingContractNumber
  const vin = insurance.vehicle?.vin ?? leasingParams?.vehicle?.vin ?? undefined
  const { data: stornoInfo } = useGetManualStornoContractsInfo(isVwfs && insurance.insuranceId ? insurance.insuranceId : undefined)

  const setConfirmCsob = async (paramsId: string, offerId: number) => {
    await confirmContractAsync({ paramsId: paramsId, offerId: offerId })
      .then((result) => {
        if (result.status === 200 && result.data) {
          if (result.data.success) {
            setContractConfirmed(true)
          }
          else {
            setContractConfirmInfo(result.data.error ?? 'Zjištění stavu podepsání smlouvy selhalo')
          }
        }
      })
      .catch((e) =>
        alert('Zjištění stavu podepsání smlouvy selhalo')
      )
  }

  const completedOverviewData = useMemo(() => {
    const result: CompletedOverviewItemProps[] = [
      {
        label: 'Číslo pojistné smlouvy',
        value: insuranceParams!.generalParams.contractNumber,
      },
      {
        label: 'Datum sjednání',
        value: formatDateTime(
          insuranceParams!.generalParams.contractCreatedDate!,
        ),
      },
    ]

    if (app != Apps.travel) {
      result.push({
        label: 'Způsob sjednání',
        value: fsEnum.find(x => x.value === insuranceParams!.generalParams.contractAcceptationType?.toString())?.description,
      })
    }

    result.push({
      label: 'Datum počátku pojištění',
      value: formatDateTimeConditional(insuranceParams!.generalParams.insuranceStartDate!),
    })

    if (app == Apps.travel) {
      result.push({
        label: 'Datum konce pojištění',
        value: formatDate(ipt.travel.insuranceEndDate!),
      })
    }

    if (cat == 2 && !isCanceled && insuranceParams?.generalParams.paymentDate) {
      result.push({
        label: 'Splatnost prvního pojistného.',
        value: (
          <Flex sx={{ gap: 4 }}>
            <CompletedOverviewValue>
              {insuranceParams?.generalParams.paymentDateSpecial
                ? insuranceParams.generalParams.paymentDateSpecial
                : formatDateTimeConditional(new Date(insuranceParams.generalParams.paymentDate))}
            </CompletedOverviewValue>
            {insurance?.paymentUrl && (
              <Link as={'a'} href={insurance.paymentUrl} target='_blank' variant="noDecoration">
                <Button>
                  Uhradit pojistné
                </Button>
              </Link>
            )}
          </Flex>
        ),
        warning: 'Pokud nebude první pojistné uhrazeno, PS nevznikne!<br/>Bližší informace ke splatnosti viz Návrh smlouvy.',
        sx: {
          gridColumn: {
            lg: 'span 2',
          },
        },
      })
    }

    switch (insurance.policyStatus) {

      case PolicyStatusType.Cancelled:
        result.push({
          label: 'Smlouva byla stornována',
          value: formatDate(insurance.contractCanceledDate!),
          isError: true
        })
        break
      case PolicyStatusType.PendingCancellation:
        result.push({
          label: 'Smlouva bude stornována',
          value: formatDate(insurance.requestedEndDate!),
          isError: true
        })
        break
      case PolicyStatusType.CancellationInProgress:
        result.push({
          label: 'Storno smlouvy probíhá',
          value: formatDate(insurance.requestedEndDate!),
          isError: true
        })
        break
      case PolicyStatusType.PendingActivation: break
      case PolicyStatusType.ActivationInProgress: break
      case PolicyStatusType.Updated: break
      case PolicyStatusType.Created: break
      case PolicyStatusType.Active:
      case PolicyStatusType.NotCreated:
      default:
        if (currentPartner?.partner === 'VWFS')
          result.push({
            label: 'Stav smlouvy',
            value: getPolicyStatusName(insurance.policyStatus)
          })
        break
    }


    // // Aktuální stav smlouvy (VWFS) – u čekajících na storno doplníme datum ukončení
    // if (isVwfs && insurance.policyStatus !== undefined && insurance.policyStatus !== PolicyStatusType.NotCreated) {
    //   const statusName = getPolicyStatusName(insurance.policyStatus)
    //   const pendingDate = insurance.requestedEndDate ?? insurance.expectedEndDate ?? insurance.insuranceEndDate
    //   const isPendingCancel = insurance.policyStatus === PolicyStatusType.PendingCancellation
    //     || insurance.policyStatus === PolicyStatusType.CancellationInProgress
    //   result.push({
    //     label: 'Stav smlouvy',
    //     value: isPendingCancel && pendingDate ? `${statusName} k ${formatDate(pendingDate)}` : statusName,
    //   })
    // }

    return result
  }, [insuranceParams, cat, fsEnum, isCanceled, dpEnum, meetingRecord, insurance, isVwfs])

  const additionalInfoData = useMemo(() => {
    const result = [...initialAdditionalInfoData]

    if (!insurance.contractEmailSent && !!insurance.contractNumber && insurance.contractAcceptationType == ContractAcceptationType.Payment) {
      result.push({
        label: 'Email',
        warning: (
          <Flex sx={{ alignItems: 'center', gap: 4 }}>
            <WarningIcon />
            <Stack gap={0}>
              <Text sx={{ color: 'red', fontSize: 'sm' }}>Pojistníkovi nebyl odeslán email s dokumentací ke sjednané smlouvě.</Text>
              <Text sx={{ color: 'red', fontSize: 'sm' }}>Email můžete odeslat pomocí tlačítka 'Odeslat e-mail'.</Text>
            </Stack>
          </Flex>
        )
      })
    }


    if ((vehicleInspectionData?.data.length ?? 0) > 0) {
      vehicleInspectionData?.data.forEach((x, i) => {
        result.push({
          label: i == 0 ? 'Prohlídka vozidla' : undefined,
          warning: !!x.text && x.text,
          value: (
            <>
              {x.uploadUrl && (
                <Link
                  as="a"
                  variant="underline"
                  target="_blank"
                  href={x.uploadUrl}
                >
                  Uploadovat fotografie lze zde
                </Link>
              )}
              {x.description && x.description}
            </>
          ),
        })
      })
    }

    const infos = offer?.infos?.filter(x => x.message && x.message.indexOf('foto') === -1 && x.value >= 1000) ?? []
    if (infos.length > 0)
      result.push({
        label: 'Doplňující informace',
        value: (
          <VStack sx={{ gap: 4 }} alignItems='flex-start'>
            {infos.map((x, i) => (
              <Flex key={i} gap={4}>
                <InfoBadge>!</InfoBadge>
                <Text fontSize='md' fontWeight='bold' dangerouslySetInnerHTML={{ __html: x.message }}></Text>
              </Flex>
            ))}
          </VStack>
        )
      })

    return result
  }, [initialAdditionalInfoData, insurance, vehicleInspectionData, offer?.infos])


  const sjednaniInfoData: CompletedOverviewItemProps[] = []
  if (
    insurance && !isCanceled && !contractConfirmed &&
    insurance.contractAcceptationType == ContractAcceptationType.DigitalSignature
  ) {
    const instructions = getDigitalSignatureInstructions(
      offer.insurerId,
      meetingRecord?.digitalSignatureType,
    )
    const instructions2 = getDigitalSignatureSecondInstructions(
      offer.insurerId,
      meetingRecord?.digitalSignatureType,
    )
    const dpDescription = dpEnum
      .find((x) => x.id == (meetingRecord?.digitalSignatureType ?? 0))
      ?.description?.toLowerCase()
    let signatureUrl = insurance.digitalSignatureUrl
    let signatureSecondUrl = null
    let signatureGcpUrl = null
    if (instructions2.length > 0 && signatureUrl && signatureUrl?.indexOf('|') != -1) {
      const urls = signatureUrl!.split('|')
      signatureUrl = urls[0]
      signatureSecondUrl = urls[1]
    }
    if (offer.insurerId == Insurer.GeneraliCeskaPojistovna) {
      signatureGcpUrl = signatureUrl
      signatureUrl = undefined
    }

    sjednaniInfoData.push({
      label: null,
      value: (
        <Flex gap={4}>
          <InfoBadge>!</InfoBadge>
          <Box>
            <Text fontSize='md' sx={{ pb: 4 }}>Doplňující informace k podpisu smlouvy - podpis <Text as="span" sx={{ fontWeight: 600 }}>{dpDescription}</Text></Text>

            {signatureUrl &&
              <>
                <Text sx={{ pb: 4 }}>Pokud sjednáváte na zařízení s dotykovou obrazovkou (mobil, tablet) můžete přímo přejít do
                  <Link as={'a'} target='_blank' href={signatureUrl} sx={{ color: 'primary.500', fontWeight: 'medium', paddingLeft: '5px' }}>aplikace pro podepsání {signatureSecondUrl ? 'předsmluvního dokumentu' : 'smlouvy'}</Link>.
                </Text>
                <Text sx={{ pb: 3 }}>V opačném případě naskenujte QR kód a pokračujte dle kroků níže.</Text>
                <Flex gap={4}>
                  <Box sx={{ maxWidth: '128px' }}>
                    <QRCode value={signatureUrl} viewBox={'0 0 256 256'} level='M' size={256} style={{ height: 'auto', maxWidth: '100%', width: '100%' }} />
                  </Box>
                  <Box>
                    <OrderedList>
                      {instructions.map((instruction) => { return (<ListItem>{instruction}</ListItem>) })}
                    </OrderedList>
                  </Box>
                </Flex>
              </>
            }

            {signatureSecondUrl &&
              <>
                <Text sx={{ pb: 4, pt: 4 }}>Dále na zařízení s dotykovou obrazovkou (mobil, tablet) můžete přejděte do
                  <Link as={'a'} target='_blank' href={signatureSecondUrl} sx={{ color: 'primary.500', fontWeight: 'medium', paddingLeft: '5px' }}>aplikace pro podepsání smlouvy</Link>.
                </Text>
                <Text sx={{ pb: 3 }}>Nebo naskenujte QR kód a pokračujte dle kroků níže.</Text>
                <Flex gap={4}>
                  <Box sx={{ maxWidth: '128px' }}>
                    <QRCode value={signatureSecondUrl} viewBox={'0 0 256 256'} level='M' size={256} style={{ height: 'auto', maxWidth: '100%', width: '100%' }} />
                  </Box>
                  <Box>
                    <OrderedList>
                      {instructions2.map((instruction) => { return (<ListItem>{instruction}</ListItem>) })}
                    </OrderedList>
                  </Box>
                </Flex>
              </>
            }

            {!!signatureGcpUrl && (
              <Text>
                Přejděte do
                <Link
                  as={'a'}
                  target="_blank"
                  href={signatureGcpUrl}
                  sx={{
                    color: 'primary.500',
                    fontWeight: 'medium',
                    paddingLeft: '5px',
                    textDecoration: 'underline',
                  }}
                >
                  aplikace pro podepsání smlouvy
                </Link>
                &nbsp;a postupujte dle kroků níže.
              </Text>
            )}

            {!signatureUrl && (
              <OrderedList>
                {instructions.map((instruction) => (
                  <ListItem key={instruction}>{instruction}</ListItem>
                ))}
              </OrderedList>
            )}

            {offer?.insurerId == Insurer.Csob && (
              <>
                <Text sx={{ fontWeight: '600', mt: 4 }}>Nakonec je nutno potvrdit podepsání dokumentů tlačítkem níže. Bez tohoto potvrzení nedojde k aktivaci pojistné smlouvy v pojišťovně.</Text>
                <Flex sx={{ w: '100%', placeContent: 'start', alignItems: 'baseline' }}>
                  <Button
                    onClick={() => setConfirmCsob(insuranceParams!.generalParams.paramsId!.toString(), offer.offerId)}
                    sx={{ mr: 4 }}
                    isLoading={isConfirmingContract}
                  >Potvrdit podepsání</Button>
                  {contractConfirmInfo && <Text>{contractConfirmInfo}</Text>}
                </Flex>
              </>
            )}
          </Box>
        </Flex>
      ),
    })
  }

  if (
    insurance && !isCanceled && contractConfirmed &&
    insurance.contractAcceptationType == ContractAcceptationType.DigitalSignature
  ) {
    sjednaniInfoData.push({
      label: null,
      value: (
        <Flex gap={4}>
          <InfoBadge>!</InfoBadge>
          <Box>
            <Text fontSize="md" sx={{ pb: 4 }}>
              Smlouva byla digitálně podepsána
            </Text>
          </Box>
        </Flex>
      ),
    })
  }

  const sidebarSectionItems: ReactNode[] = []
  if (app != Apps.leasing && // pro leasing se email neposila
    insuranceParams?.generalParams?.contractNumber &&
    !(currentPartner?.partner == 'Respect')) { // Respect má pouze tlačítko pro odeslání emailu v záhlaví
    sidebarSectionItems.push(<SendContractContextProvider insuranceId={insurance.insuranceId ?? undefined}>
      <SendContractModal />
      <SendContractButton insuranceParams={insuranceParams} insurance={insurance} />
    </SendContractContextProvider>
    )
  }

  if (insurance.contractNumber && !isCanceled && insurance.insuranceId && isVwfs) {
    sidebarSectionItems.push(
      <VwfsStornoActions
        insurance={insurance}
        insuranceParams={insuranceParams as InsuranceParamsLeasingDto}
        leasingContractNumber={lcs}
        callback={onContractChange}
      />
    )
  }
  else if (insurance.cancellationOptions?.canCancel) {
    sidebarSectionItems.push(
      <Flex mb={4}>
        <CancelContract
          callback={onContractChange}
          insuranceId={insurance.insuranceId!}
        />
      </Flex>)
  }
  else if (insurance.cancellationOptions?.canReactivate) {
    sidebarSectionItems.push(
      <Flex mb={4}>
        <ReactivateContract
          callback={onContractChange}
          insuranceId={insurance.insuranceId!}
        />
      </Flex>)
  }

  if (stornoInfo?.canCreateStock) {
    sidebarSectionItems.push(
      <Flex mb={4}>
        <CreateStockPs
          key="createStockPs"
          insuranceParamsId={insuranceParamsId!}
          isDisabled={!stornoInfo.canCreateStock}
        />
      </Flex>
    )
  }

  let japDatesData: CompletedOverviewItemProps[] | undefined = undefined
  if (insuranceParams?.generalParams?.contractNumber && currentPartner?.partner == 'Jap' && !isPublicPoleas) {
    japDatesData = [
      {
        label: 'Datum převzetí pojišťovacím makléřem',
        value: (insuranceParams!.generalParams.contractAcceptedDate ? formatDate(insuranceParams!.generalParams.contractAcceptedDate) : 'nezadáno')
      },
      {
        label: 'Datum zaplacení 1. splátky',
        value: (insuranceParams!.generalParams.firstPaymentDate ? formatDate(insuranceParams!.generalParams.firstPaymentDate) : 'nezadáno')
      },
      {
        label: 'Datum vyplacení provize',
        value: (insuranceParams!.generalParams.commissionPaidDate ? formatDate(insuranceParams!.generalParams.commissionPaidDate) : 'nezadáno')
      }
    ]
  }

  return (
    <InsuranceSagaProvider insuranceId={insurance.insuranceId ?? undefined}>
      <Stack rowGap={12}>
        <Information
          number={1}
          sectionTitle='Informace ke sjednané smlouvě'
          offer={offer}
          insurance={insurance}
          recapProps={{
            heading: app == Apps.travel ? '' : 'Pojistné/rok',
            rows: app == Apps.travel
              ? getPackageRecapData(offer as OfferTravel, 'Rozsah pojištění')
              : getRecapData(offer, app!, currency),
            total: priceFormat(offer.price),
            totalProps: { sx: { border: 'none' } }
          }}
          completedOverviewData={completedOverviewData}
          additionalInfoData={additionalInfoData}
          sidebarSectionItems={sidebarSectionItems}
          additionalDatesTabData={japDatesData}
          sjednaniInfoData={sjednaniInfoData}
          // kvuli infu pro digitalni podpis udelat grid pouze s jedním sloupcem
          sjednaniInfoProps={{
            sx: {
              gridTemplateColumns: {
                base: '100%',
                md: '1fr',
                xl: 'auto'
              }
            }
          }}
        />

        <Documents insuranceParams={insuranceParams!} offer={offer} />
      </Stack>
    </InsuranceSagaProvider>
  )
}

export const SendContractButton = ({ insuranceParams, insurance }: {
  insuranceParams?: IInsuranceParams,
  insurance?: InsuranceBase
}) => {
  const { successToast, toast } = useCustomToast()
  const { app } = useApp()
  const isPublicPoleas = app == Apps.publicPoleas

  const { mutateAsync: sendContractEmail, isPending: isSendingContractEmail } = useSendContractEmail()

  const handleSendContractEmailClick = () => {
    sendContractEmail({
      paramsId: insuranceParams?.generalParams.paramsId,
      insuranceId: insurance?.insuranceId
    })
      .then(successToast('Email byl úspěšně odeslán.'))
      .catch((r: AxiosError<SendContractEmailResponse>) => {
        toast({
          status: 'error',
          title: 'Nepodařilo se odeslat e-mail',
          description: r.response?.data.errors?.join(', ')
        })
      })
  }

  const { onOpen } = useSendContractContext()

  if (!insuranceParams)
    return null

  const showDirectEmailButton = !!insuranceParams.policyHolder.person.email

  return <Flex my={4} mx={4} justifyContent='stretch'>
    {showDirectEmailButton && (
      <CustomTooltip boxProps={{ w: 'full' }} label={'Odeslat e-mail klientovi na adresu ' + insuranceParams.policyHolder.person.email} hasArrow>
        <Button type="button" w='full'
          borderRightRadius={0}
          onClick={handleSendContractEmailClick} isLoading={isSendingContractEmail}
          minH={12}>
          <HStack gap={4} ml={-4} w={'full'} justifyContent='center'>
            <Icon as={FontAwesomeIcon} icon={faPaperPlane} size='lg' />

            {isPublicPoleas
              ? <Text color='inherit'>Odeslat e-mail</Text>
              : <Text w='full' color='inherit'>Odeslat klientovi na&nbsp;e&#8209;mail</Text>
            }
          </HStack>
        </Button>
      </CustomTooltip>
    )}

    {!isPublicPoleas &&
      <CustomTooltip label='Upravit a odeslat na e-mail' h='full' boxProps={{ w: !showDirectEmailButton ? '100%' : undefined }} hasArrow>
        <Button type="button" variant='outline' px={4} h='full'
          borderLeftRadius={showDirectEmailButton ? 0 : 'sm'}
          borderColor='primary.500'
          color='primary.500'
          _hover={{ background: 'gray.100' }}
          onClick={onOpen}
          minH={12}
          w={!showDirectEmailButton ? '100%' : undefined}
        >
          <HStack gap={4}>
            <Box pos='relative' h='100%'>
              <Icon as={FontAwesomeIcon} icon={faPen} position='absolute' top={2} left={2} />
              <Icon as={FontAwesomeIcon} icon={faFileLines} size='lg' />
            </Box>
            {!showDirectEmailButton && <Text w='full' color='inherit'>Upravit a odeslat na e-mail</Text>}
          </HStack>
        </Button>
      </CustomTooltip>
    }
  </Flex >
}